import React, { useState, useEffect } from "react"
import { connect } from "react-redux"
import {
  Table,
  Card,
  Row,
  Col,
  Space,
  Typography,
  Statistic,
  Tag,
  Modal,
  Descriptions,
  Button,
  Spin,
  Empty,
  Divider,
  Badge,
  Tooltip,
  List,
} from "antd"
import {
  CalendarOutlined,
  EyeOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  BankOutlined,
  UserOutlined,
  HomeOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  DownOutlined,
} from "@ant-design/icons"
import { withTranslation } from "react-i18next"
import { getEngPaymentTask, getEngPaymentList } from "store/actions"
import { getEngPaymentInvoiceApi } from "helpers/backend_helper"
import { serializeQuery } from "helpers/service_helper"

const { Title, Text } = Typography

const EngPaymentDashboard = props => {
  const {
    lstEngPaymentTask,
    lstEngPayment,
    loading,
    onGetEngPaymentTasks,
    onGetEngPaymentList,
  } = props

  const [selectedTaskId, setSelectedTaskId] = useState(null)
  const [invoiceModalVisible, setInvoiceModalVisible] = useState(false)
  const [invoiceData, setInvoiceData] = useState([])
  const [invoiceLoading, setInvoiceLoading] = useState(false)
  const [selectedPayment, setSelectedPayment] = useState(null)
  const [taskLoading, setTaskLoading] = useState(false)
  const [expandedInvoiceId, setExpandedInvoiceId] = useState(null)

  useEffect(() => {
    onGetEngPaymentTasks()
  }, [])

  // وقتی دوره انتخاب شد، لیست پرداخت را دریافت کن
  const handleTaskSelect = async (taskId) => {
    setTaskLoading(true)
    setSelectedTaskId(taskId)
    const params = serializeQuery({ engPaymentTaskId: taskId })
    await onGetEngPaymentList(params)
    setTaskLoading(false)
    
    // Auto scroll to payment details on mobile
    if (window.innerWidth <= 768) {
      setTimeout(() => {
        const detailsElement = document.getElementById('payment-details-section')
        if (detailsElement) {
          detailsElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 300)
    }
  }

  // دریافت جزئیات فاکتور
  const handleViewInvoiceDetails = async (payment) => {
    setSelectedPayment(payment)
    setInvoiceModalVisible(true)
    setInvoiceLoading(true)
    
    try {
      const response = await getEngPaymentInvoiceApi(selectedTaskId)
      setInvoiceData(response || [])
    } catch (error) {
      console.error("Error fetching invoice:", error)
      setInvoiceData([])
    } finally {
      setInvoiceLoading(false)
    }
  }

  const formatNumber = num => {
    return String(num || 0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  }

  // محاسبه آمار
  const calculateStats = () => {
    if (!lstEngPayment || lstEngPayment.length === 0) {
      return { totalAmount: 0, isPaid: false }
    }
    const payment = lstEngPayment[0]
    return {
      totalAmount: payment?.sumAmountWithFish || 0,
      isPaid: payment?.approved || false,
      amountSystem: payment?.amountSystem || 0,
      deductions: (payment?.deduction1 || 0) + (payment?.deduction2 || 0) + (payment?.deduction3 || 0) + (payment?.deduction4 || 0),
    }
  }

  const stats = calculateStats()

  // ستون‌های جدول دوره‌ها
  const taskColumns = [
    {
      title: "دوره",
      dataIndex: "period",
      key: "period",
      render: (text) => (
        <Tag color="blue" style={{ fontSize: 14 }}>
          دوره {text}
        </Tag>
      ),
    },
    {
      title: "تاریخ ایجاد",
      dataIndex: "solarCreated",
      key: "solarCreated",
      render: (text) => (
        <Space>
          <CalendarOutlined />
          <span>{text}</span>
        </Space>
      ),
    },
    {
      title: "از تاریخ",
      dataIndex: "fromSolar",
      key: "fromSolar",
    },
    {
      title: "تا تاریخ",
      dataIndex: "toSolar",
      key: "toSolar",
    },
    {
      title: "تاریخ پرداخت",
      dataIndex: "solarPay",
      key: "solarPay",
      render: (text) => (
        text ? (
          <Tag color="green" icon={<CheckCircleOutlined />}>
            {text}
          </Tag>
        ) : (
          <Tag color="orange" icon={<ClockCircleOutlined />}>
            در انتظار
          </Tag>
        )
      ),
    },
    {
      title: "خالص پرداختی",
      dataIndex: "sumAmountSystem",
      key: "sumAmountSystem",
      render: (text) => (
        <Text strong style={{ color: '#52c41a' }}>
          {formatNumber(text)} ریال
        </Text>
      ),
      ellipsis: true,
    },
  ]

  // ستون‌های جدول فاکتورها (داخل مودال)
  const invoiceColumns = [
    {
      title: "شماره پرونده",
      dataIndex: "fileNumber",
      key: "fileNumber",
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "مالک",
      dataIndex: "landlordName",
      key: "landlordName",
    },
    {
      title: "نوع پرداخت",
      dataIndex: "invoicePayTypeName",
      key: "invoicePayTypeName",
      render: (text) => <Tag color="purple">{text}</Tag>,
    },
    {
      title: "مبلغ",
      dataIndex: "invoiceAmount",
      key: "invoiceAmount",
      render: (text) => (
        <Text strong style={{ color: '#52c41a' }}>
          {formatNumber(text)} ریال
        </Text>
      ),
    },
    {
      title: "شهر",
      dataIndex: "cityName",
      key: "cityName",
    },
    {
      title: "منطقه",
      dataIndex: "sectionName",
      key: "sectionName",
    },
  ]

  // کارت آمار
  const StatCard = ({ title, value, icon, color, suffix, extra }) => (
    <Card
      size="small"
      style={{
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        border: `1px solid ${color}20`,
        background: `linear-gradient(135deg, ${color}08 0%, white 100%)`,
        height: '100%',
      }}
    >
      <Statistic
        title={<Text style={{ color: '#666', fontSize: 12 }}>{title}</Text>}
        value={value}
        suffix={suffix}
        prefix={icon}
        valueStyle={{ color, fontSize: 20, fontWeight: 600 }}
      />
      {extra && <div style={{ marginTop: 8 }}>{extra}</div>}
    </Card>
  )

  return (
    <div style={{ padding: '16px', background: '#f5f7fa', minHeight: '100%' }}>
      {/* هدر */}
      <Card
        style={{
          marginBottom: 16,
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        }}
      >
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Title level={4} style={{ margin: 0 }}>پرداخت‌های من</Title>
            </Space>
          </Col>
          <Col>
            <Text type="secondary">
              {lstEngPaymentTask?.length || 0} دوره پرداختی
            </Text>
          </Col>
        </Row>
      </Card>

      {/* جدول دوره‌ها */}
      <Card
        style={{
          marginBottom: 16,
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        }}
        title={
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Space>
              <CalendarOutlined style={{ color: '#1890ff' }} />
              <Text strong>دوره‌های پرداخت</Text>
              <Badge count={lstEngPaymentTask?.length || 0} style={{ backgroundColor: '#1890ff' }} />
            </Space>
            <Text type="secondary" style={{ fontSize: 12 }}>برای مشاهده جزئیات، یک دوره را انتخاب کنید</Text>
          </Space>
        }
      >
        {/* Desktop Table View */}
        <div className="tasks-table-desktop">
          <Table
            loading={loading}
            dataSource={lstEngPaymentTask}
            columns={taskColumns}
            rowKey="id"
            size="middle"
            pagination={{
              pageSize: 5,
              showSizeChanger: false,
              showTotal: (total, range) => `${range[0]}-${range[1]} از ${total} دوره`,
              hideOnSinglePage: true,
            }}
            onRow={(record) => ({
              onClick: () => handleTaskSelect(record.id),
              style: {
                cursor: 'pointer',
                background: selectedTaskId === record.id ? '#e6f7ff' : 'inherit',
              },
            })}
            rowClassName={(record) => 
              selectedTaskId === record.id ? 'selected-row' : ''
            }
            locale={{ emptyText: <Empty description="هیچ دوره پرداختی یافت نشد" /> }}
          />
        </div>

        {/* Mobile Card View */}
        <div className="tasks-cards-mobile">
          <Spin spinning={loading || taskLoading} tip={taskLoading ? "در حال بارگذاری جزئیات..." : "در حال بارگذاری..."}>
            {lstEngPaymentTask && lstEngPaymentTask.length > 0 ? (
              <List
                dataSource={lstEngPaymentTask}
                renderItem={(task) => {
                  const isSelected = selectedTaskId === task.id
                  const isProcessing = taskLoading && isSelected
                  
                  return (
                    <List.Item
                      key={task.id}
                      style={{ padding: 0, border: 'none', marginBottom: 12 }}
                    >
                      <Card
                        size="small"
                        onClick={() => !taskLoading && handleTaskSelect(task.id)}
                        className={`task-card-mobile ${isSelected ? 'selected' : ''} ${isProcessing ? 'processing' : ''}`}
                        style={{
                          width: '100%',
                          borderRadius: 8,
                          cursor: taskLoading ? 'not-allowed' : 'pointer',
                          border: isSelected ? '2px solid #1890ff' : '1px solid #d9d9d9',
                          background: isSelected ? '#e6f7ff' : '#fff',
                          boxShadow: isSelected ? '0 4px 12px rgba(24, 144, 255, 0.2)' : '0 2px 4px rgba(0,0,0,0.06)',
                          opacity: taskLoading && !isSelected ? 0.6 : 1,
                          transition: 'all 0.3s ease',
                          transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                        }}
                        hoverable={!taskLoading}
                      >
                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                          {/* Header */}
                          <Row justify="space-between" align="middle">
                            <Col>
                              <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px' }}>
                                دوره {task.period}
                              </Tag>
                            </Col>
                            <Col>
                              {task.solarPay ? (
                                <Tag color="green" icon={<CheckCircleOutlined />}>
                                  پرداخت شده
                                </Tag>
                              ) : (
                                <Tag color="orange" icon={<ClockCircleOutlined />}>
                                  در انتظار
                                </Tag>
                              )}
                            </Col>
                          </Row>

                          <Divider style={{ margin: '8px 0' }} />

                          {/* Details */}
                          <Row gutter={[8, 8]}>
                            <Col span={12}>
                              <Text type="secondary" style={{ fontSize: 12 }}>تاریخ ایجاد:</Text>
                              <br />
                              <Space size={4}>
                                <CalendarOutlined style={{ fontSize: 12, color: '#1890ff' }} />
                                <Text style={{ fontSize: 13 }}>{task.solarCreated}</Text>
                              </Space>
                            </Col>
                            <Col span={12}>
                              <Text type="secondary" style={{ fontSize: 12 }}>تاریخ پرداخت:</Text>
                              <br />
                              <Text style={{ fontSize: 13 }}>
                                {task.solarPay || '-'}
                              </Text>
                            </Col>
                            <Col span={12}>
                              <Text type="secondary" style={{ fontSize: 12 }}>از تاریخ:</Text>
                              <br />
                              <Text style={{ fontSize: 13 }}>{task.fromSolar}</Text>
                            </Col>
                            <Col span={12}>
                              <Text type="secondary" style={{ fontSize: 12 }}>تا تاریخ:</Text>
                              <br />
                              <Text style={{ fontSize: 13 }}>{task.toSolar}</Text>
                            </Col>
                            <Col span={24}>
                              <Divider style={{ margin: '8px 0' }} />
                              <Text type="secondary" style={{ fontSize: 12 }}>خالص پرداختی:</Text>
                              <br />
                              <Text strong style={{ color: '#52c41a', fontSize: 16 }}>
                                {formatNumber(task.sumAmountSystem)} ریال
                              </Text>
                            </Col>
                          </Row>
                        </Space>
                      </Card>
                    </List.Item>
                  )
                }}
              />
            ) : (
              <Empty description="هیچ دوره پرداختی یافت نشد" />
            )}
          </Spin>
        </div>
      </Card>

      {/* نمایش جزئیات پرداخت انتخاب شده */}
      <div id="payment-details-section">
      {selectedTaskId && lstEngPayment && lstEngPayment.length > 0 && (
        <>
          {/* آمار پرداخت */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} md={6}>
              <StatCard
                title="مبلغ سیستمی"
                value={formatNumber(stats.amountSystem)}
                color="#1890ff"
                suffix="ریال"
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <StatCard
                title="کسورات"
                value={formatNumber(stats.deductions)}
                icon={<BankOutlined />}
                color="#ff4d4f"
                suffix="ریال"
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <StatCard
                title="خالص پرداختی"
                value={formatNumber(stats.totalAmount)}
                icon={<CheckCircleOutlined />}
                color="#52c41a"
                suffix="ریال"
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <StatCard
                title="وضعیت"
                value={stats.isPaid ? "پرداخت شده" : "در انتظار"}
                icon={stats.isPaid ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                color={stats.isPaid ? "#52c41a" : "#fa8c16"}
              />
            </Col>
          </Row>

          {/* کارت جزئیات پرداخت */}
          <Card
            style={{
              borderRadius: 12,
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            }}
            title={
              <Space>
                <FileTextOutlined style={{ color: '#52c41a' }} />
                <Text strong>جزئیات پرداخت</Text>
              </Space>
            }
            extra={
              <Button
                type="primary"
                icon={<EyeOutlined />}
                onClick={() => handleViewInvoiceDetails(lstEngPayment[0])}
              >
                مشاهده فاکتورها
              </Button>
            }
          >
            {lstEngPayment.map((payment, index) => (
              <Descriptions
                key={index}
                bordered
                size="small"
                column={{ xs: 1, sm: 2, md: 3 }}
              >
                <Descriptions.Item label="نام">
                  <Space>
                    <UserOutlined />
                    {payment.fullName}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="مبلغ سیستمی">
                  <Text type="success">{formatNumber(payment.amountSystem)} ریال</Text>
                </Descriptions.Item>
                <Descriptions.Item label="فیش کاغذی">
                  {formatNumber(payment.addition1)} ریال
                </Descriptions.Item>
                <Descriptions.Item label="کسورات نظام">
                  <Text type="danger">{formatNumber(payment.deduction1)} ریال</Text>
                </Descriptions.Item>
                <Descriptions.Item label="کسورات بیمه">
                  <Text type="danger">{formatNumber(payment.deduction2)} ریال</Text>
                </Descriptions.Item>
                <Descriptions.Item label="کسورات علی‌الحساب">
                  <Text type="danger">{formatNumber(payment.deduction3)} ریال</Text>
                </Descriptions.Item>
                <Descriptions.Item label="سایر کسورات">
                  <Text type="danger">{formatNumber(payment.deduction4)} ریال</Text>
                </Descriptions.Item>
                <Descriptions.Item label="خالص پرداختی" span={2}>
                  <Text strong style={{ fontSize: 16, color: '#52c41a' }}>
                    {formatNumber(payment.sumAmountWithFish)} ریال
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="شماره حساب">
                  <Tag color="blue">{payment.engineer?.bankAccountNumber || '-'}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="پرداخت با فیش">
                  {payment.payByBankReceipt ? (
                    <Tag color="green">بله</Tag>
                  ) : (
                    <Tag color="blue">خیر - بانکی</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="وضعیت پرداخت">
                  {payment.approved ? (
                    <Tag color="success" icon={<CheckCircleOutlined />}>پرداخت شده</Tag>
                  ) : (
                    <Tag color="warning" icon={<ClockCircleOutlined />}>در انتظار پرداخت</Tag>
                  )}
                </Descriptions.Item>
              </Descriptions>
            ))}
          </Card>
        </>
      )}
      </div>

      {/* پیام در صورت عدم انتخاب دوره */}
      {!selectedTaskId && (
        <Card
          style={{
            borderRadius: 12,
            textAlign: 'center',
            padding: 40,
          }}
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Text type="secondary">
                لطفاً یک دوره پرداخت را از جدول بالا انتخاب کنید
              </Text>
            }
          />
        </Card>
      )}

      {/* مودال فاکتورها */}
      <Modal
        title={
          <Space>
            <FileTextOutlined style={{ color: '#1890ff' }} />
            <span>جزئیات فاکتورها</span>
          </Space>
        }
        open={invoiceModalVisible}
        onCancel={() => setInvoiceModalVisible(false)}
        width={window.innerWidth <= 768 ? '100%' : 1000}
        style={window.innerWidth <= 768 ? { top: 20, paddingBottom: 0 } : {}}
        bodyStyle={window.innerWidth <= 768 ? { paddingTop: 16 } : {}}
        footer={[
          <Button key="close" onClick={() => setInvoiceModalVisible(false)}>
            بستن
          </Button>
        ]}
      >
        <Spin spinning={invoiceLoading}>
          {invoiceData.length > 0 ? (
            <>
              {/* خلاصه */}
              <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={12} md={8}>
                  <Card
                    size="small"
                    style={{
                      borderRadius: 10,
                      background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
                      border: '1px solid #667eea30',
                      boxShadow: '0 2px 8px rgba(102, 126, 234, 0.12)',
                    }}
                  >
                    <Statistic
                      title={<Text style={{ color: '#666', fontSize: 13, fontWeight: 500 }}>تعداد فاکتور</Text>}
                      value={invoiceData.length}
                      suffix="مورد"
                      prefix={<FileTextOutlined style={{ color: '#667eea' }} />}
                      valueStyle={{ color: '#667eea', fontSize: 24, fontWeight: 700 }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Card
                    size="small"
                    style={{
                      borderRadius: 10,
                      background: 'linear-gradient(135deg, #52c41a15 0%, #73d13d15 100%)',
                      border: '1px solid #52c41a30',
                      boxShadow: '0 2px 8px rgba(82, 196, 26, 0.12)',
                    }}
                  >
                    <Statistic
                      title={<Text style={{ color: '#666', fontSize: 13, fontWeight: 500 }}>جمع کل مبلغ</Text>}
                      value={formatNumber(invoiceData.reduce((sum, inv) => sum + (inv.invoiceAmount || 0), 0))}
                      suffix="ریال"
                      prefix={<BankOutlined style={{ color: '#52c41a' }} />}
                      valueStyle={{ color: '#52c41a', fontSize: 24, fontWeight: 700 }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Card
                    size="small"
                    style={{
                      borderRadius: 10,
                      background: 'linear-gradient(135deg, #1890ff15 0%, #40a9ff15 100%)',
                      border: '1px solid #1890ff30',
                      boxShadow: '0 2px 8px rgba(24, 144, 255, 0.12)',
                    }}
                  >
                    <Statistic
                      title={<Text style={{ color: '#666', fontSize: 13, fontWeight: 500 }}>میانگین مبلغ</Text>}
                      value={formatNumber(Math.round(invoiceData.reduce((sum, inv) => sum + (inv.invoiceAmount || 0), 0) / invoiceData.length))}
                      suffix="ریال"
                      prefix={<CheckCircleOutlined style={{ color: '#1890ff' }} />}
                      valueStyle={{ color: '#1890ff', fontSize: 24, fontWeight: 700 }}
                    />
                  </Card>
                </Col>
              </Row>

              <Divider />

              {/* جدول فاکتورها */}
              <div className="invoice-table-desktop">
                <Table
                  dataSource={invoiceData}
                  columns={invoiceColumns}
                  rowKey={(record, index) => index}
                  size="small"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ['5', '10', '20', '50'],
                    showTotal: (total, range) => `${range[0]}-${range[1]} از ${total} فاکتور`,
                  }}
                  expandable={{
                    expandedRowRender: (record) => (
                      <Descriptions size="small" column={2} bordered>
                        <Descriptions.Item label="پارامتر بازرسی">
                          {record.inspectionParameterTypeName}
                        </Descriptions.Item>
                        <Descriptions.Item label="نوع لوله‌کشی">
                          {record.pipingTypeName}
                        </Descriptions.Item>
                        <Descriptions.Item label="مجری">
                          <Space>
                            <TeamOutlined />
                            {record.executorName}
                          </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label="تاریخ تحویل به دفتر">
                          {record.allocationDeliverToOfficeSolarDate}
                        </Descriptions.Item>
                        <Descriptions.Item label="تاریخ تایید">
                          {record.allocationAcceptedSolarDate}
                        </Descriptions.Item>
                        <Descriptions.Item label="توضیحات تخصیص">
                          {record.allocationDescription || '-'}
                        </Descriptions.Item>
                      </Descriptions>
                    ),
                  }}
                />
              </div>

              {/* لیست کارت‌های فاکتور (نمای موبایل) */}
              <div className="invoice-cards-mobile">
                <List
                  dataSource={invoiceData}
                  renderItem={(item, index) => {
                    const isExpanded = expandedInvoiceId === index
                    
                    return (
                    <List.Item key={index} style={{ padding: 0, border: 'none', marginBottom: 12 }}>
                      <Card
                        size="small"
                        style={{ 
                          width: '100%', 
                          borderRadius: 8,
                          boxShadow: isExpanded ? '0 4px 12px rgba(24, 144, 255, 0.15)' : '0 2px 8px rgba(0,0,0,0.08)',
                          border: isExpanded ? '1px solid #1890ff' : '1px solid #d9d9d9',
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                          {/* Header */}
                          <div onClick={() => setExpandedInvoiceId(isExpanded ? null : index)} style={{ cursor: 'pointer' }}>
                            <Row justify="space-between" align="middle">
                              <Col flex="auto">
                                <Space>
                                  <Tag color="blue" style={{ fontSize: 13, padding: '4px 10px' }}>
                                    <FileTextOutlined style={{ marginLeft: 4 }} />
                                    {item.fileNumber}
                                  </Tag>
                                  <Tag color="purple" style={{ fontSize: 12 }}>
                                    {item.invoicePayTypeName}
                                  </Tag>
                                </Space>
                              </Col>
                              <Col>
                                <Button
                                  type="text"
                                  size="small"
                                  icon={
                                    <DownOutlined 
                                      style={{
                                        fontSize: 12,
                                        transition: 'transform 0.3s ease',
                                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                        color: '#1890ff',
                                      }}
                                    />
                                  }
                                />
                              </Col>
                            </Row>
                            
                            {/* Owner & Amount in Header */}
                            <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                              <Col span={24}>
                                <Space size={4}>
                                  <UserOutlined style={{ fontSize: 12, color: '#1890ff' }} />
                                  <Text type="secondary" style={{ fontSize: 12 }}>مالک:</Text>
                                  <Text style={{ fontSize: 13 }} strong>{item.landlordName}</Text>
                                </Space>
                              </Col>
                              <Col span={24}>
                                <Space size={4}>
                                  <Text type="secondary" style={{ fontSize: 12 }}>مبلغ:</Text>
                                  <Text strong style={{ color: '#52c41a', fontSize: 15 }}>
                                    {formatNumber(item.invoiceAmount)} ریال
                                  </Text>
                                </Space>
                              </Col>
                            </Row>
                          </div>

                          {/* Expandable Details */}
                          {isExpanded && (
                            <div className="invoice-card-details" style={{ animation: 'expandDetails 0.3s ease-out' }}>
                              <Divider style={{ margin: '8px 0' }} />

                          {/* Location Details */}
                          <Row gutter={[8, 8]}>
                            <Col span={12}>
                              <Text type="secondary" style={{ fontSize: 12 }}>شهر:</Text>
                              <br />
                              <Space size={4}>
                                <EnvironmentOutlined style={{ fontSize: 12, color: '#fa8c16' }} />
                                <Text style={{ fontSize: 13 }}>{item.cityName}</Text>
                              </Space>
                            </Col>
                            <Col span={12}>
                              <Text type="secondary" style={{ fontSize: 12 }}>منطقه:</Text>
                              <br />
                              <Space size={4}>
                                <HomeOutlined style={{ fontSize: 12, color: '#fa8c16' }} />
                                <Text style={{ fontSize: 13 }}>{item.sectionName}</Text>
                              </Space>
                            </Col>
                          </Row>

                          <Divider style={{ margin: '8px 0' }} />

                          {/* Additional Details */}
                          <Row gutter={[8, 8]}>
                            <Col span={12}>
                              <Text type="secondary" style={{ fontSize: 11 }}>پارامتر بازرسی:</Text>
                              <br />
                              <Text style={{ fontSize: 12 }}>{item.inspectionParameterTypeName || '-'}</Text>
                            </Col>
                            <Col span={12}>
                              <Text type="secondary" style={{ fontSize: 11 }}>نوع لوله‌کشی:</Text>
                              <br />
                              <Text style={{ fontSize: 12 }}>{item.pipingTypeName || '-'}</Text>
                            </Col>
                            <Col span={24}>
                              <Space size={4}>
                                <TeamOutlined style={{ fontSize: 12, color: '#722ed1' }} />
                                <Text type="secondary" style={{ fontSize: 11 }}>مجری:</Text>
                                <Text style={{ fontSize: 12 }}>{item.executorName}</Text>
                              </Space>
                            </Col>
                            {item.allocationDeliverToOfficeSolarDate && (
                              <Col span={12}>
                                <Text type="secondary" style={{ fontSize: 11 }}>تحویل به دفتر:</Text>
                                <br />
                                <Space size={4}>
                                  <CalendarOutlined style={{ fontSize: 11, color: '#52c41a' }} />
                                  <Text style={{ fontSize: 11 }}>{item.allocationDeliverToOfficeSolarDate}</Text>
                                </Space>
                              </Col>
                            )}
                            {item.allocationAcceptedSolarDate && (
                              <Col span={12}>
                                <Text type="secondary" style={{ fontSize: 11 }}>تاریخ تایید:</Text>
                                <br />
                                <Space size={4}>
                                  <CheckCircleOutlined style={{ fontSize: 11, color: '#52c41a' }} />
                                  <Text style={{ fontSize: 11 }}>{item.allocationAcceptedSolarDate}</Text>
                                </Space>
                              </Col>
                            )}
                            {item.allocationDescription && (
                              <Col span={24}>
                                <Text type="secondary" style={{ fontSize: 11 }}>توضیحات:</Text>
                                <br />
                                <Text style={{ fontSize: 11 }} type="secondary">{item.allocationDescription}</Text>
                              </Col>
                            )}
                          </Row>
                            </div>
                          )}
                        </Space>
                      </Card>
                    </List.Item>
                    )
                  }}
                />
              </div>
            </>
          ) : (
            <Empty description="فاکتوری یافت نشد" />
          )}
        </Spin>
      </Modal>

      {/* استایل‌ها */}
      <style>{`
        .selected-row {
          background-color: #e6f7ff !important;
        }
        .selected-row:hover {
          background-color: #bae7ff !important;
        }
        .ant-table-tbody > tr:hover > td {
          background-color: #f5f5f5;
        }
        
        /* Mobile/Desktop switching for tasks */
        .tasks-cards-mobile {
          display: none;
        }
        .tasks-table-desktop {
          display: block;
        }
        
        /* Invoice modal mobile view */
        .invoice-cards-mobile {
          display: none;
        }
        
        /* Invoice table desktop view */
        .invoice-table-desktop {
          display: block;
        }
        
        /* Mobile card touch feedback */
        .task-card-mobile {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .task-card-mobile:active:not(.processing) {
          transform: scale(0.98) !important;
        }
        
        .task-card-mobile.selected {
          animation: slideInSelect 0.3s ease-out;
        }
        
        @keyframes slideInSelect {
          0% {
            transform: scale(0.95);
            opacity: 0.8;
          }
          100% {
            transform: scale(1.02);
            opacity: 1;
          }
        }
        
        .task-card-mobile.processing {
          animation: pulse 1.5s ease-in-out infinite;
        }
        
        @keyframes pulse {
          0%, 100% {
            box-shadow: 0 4px 12px rgba(24, 144, 255, 0.2);
          }
          50% {
            box-shadow: 0 4px 20px rgba(24, 144, 255, 0.4);
          }
        }
        
        @keyframes expandDetails {
          0% {
            opacity: 0;
            transform: translateY(-10px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        #payment-details-section {
          scroll-margin-top: 20px;
        }
        
        @media (max-width: 768px) {
          /* Tasks table mobile view */
          .tasks-cards-mobile {
            display: block !important;
          }
          .tasks-table-desktop {
            display: none !important;
          }
          
          /* Invoice modal mobile view */
          .invoice-cards-mobile {
            display: block !important;
          }
          .invoice-table-desktop {
            display: none !important;
          }
          
          /* Modal adjustments for mobile */
          .ant-modal {
            max-width: 100% !important;
            margin: 0 !important;
          }
          .ant-modal-body {
            max-height: calc(100vh - 120px);
            overflow-y: auto;
          }
          
          /* Reduce padding on mobile */
          .task-card-mobile .ant-card-body {
            padding: 12px;
          }
          
          /* Invoice card mobile specific */
          .invoice-cards-mobile .ant-card-body {
            padding: 12px;
          }
        }
      `}</style>
    </div>
  )
}

const mapStateToProps = ({ EngPayment }) => ({
  lstEngPaymentTask: EngPayment.lstEngPaymentTask,
  lstEngPayment: EngPayment.lstEngPayment,
  loading: EngPayment.loading,
  error: EngPayment.error,
  success: EngPayment.success,
})

const mapDispatchToProps = dispatch => ({
  onGetEngPaymentTasks: () => dispatch(getEngPaymentTask()),
  onGetEngPaymentList: (params) => dispatch(getEngPaymentList(params)),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(EngPaymentDashboard))
